import {
  IsBoolean,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  Max,
  ValidateIf,
  ValidateNested,
} from '@pristine-ts/class-validator';
import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import { CivilStatusEnum } from '../../../enums/civil-status.enum';
import { ProvinceEnum } from '../../../enums/province.enum';
import { PrescriptionDrugInsuranceEnum } from '../../../enums/prescription-drug-insurance.enum';
import { ClientQuestionnaireOptions } from '../client-questionnaire.options';
import { ClientQuestionnaireKeynameEnum } from '../../../enums/client-questionnaire/client-questionnaire-keyname.enum';
import { Quebec2023TaxQuestionnaireChildOptions } from './quebec-2023-tax-questionnaire-child.options';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';
import { Type } from 'class-transformer';
import { CalculationDate } from '../../../models/calculation.date';
import { type } from '@pristine-ts/data-mapping-common';
import { advancedFormControl, advancedFormGroup, advancedFormArray, FormControlTypeEnum } from '@magieno/common';

@translationBaseKey('quebec-2023-tax-questionnaire-options')
export class Quebec2023TaxQuestionnaireBaseOptions extends ClientQuestionnaireOptions {

  @advancedFormControl({
    type: FormControlTypeEnum.Hidden,
  })
  keyname: ClientQuestionnaireKeynameEnum = ClientQuestionnaireKeynameEnum.QuebecTaxForm2023;

  @Type(() => Quebec2023TaxQuestionnaireChildOptions)
  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 0,
    elementClassType: Quebec2023TaxQuestionnaireChildOptions,
  })
  @ValidateNested()
  children: Quebec2023TaxQuestionnaireChildOptions[] = [];

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Prénom',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  firstname: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom de famille',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  lastname: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Adresse postale',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  address: string;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de naissance',
        en: '',
      },
      tooltip: {
        fr: 'L\'âge au 31 décembre sera calculé par Optitax à partir de la date de naissance, et le questionnaire sera ajusté en fonction de l\'âge.',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  dateOfBirth: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro d\'assurance social (NAS)',
        en: '',
      },
      tooltip: {
        fr: 'Vous n\'avez pas besoin d\'utiliser de tirets ou d\'espaces lorsque vous inscrivez votre numéro d\'assurance sociale (NAS), mais vous devez inclure tous les neuf (9) chiffres. Si vous ne connaissez pas votre NAS, vous pouvez entrer 000 000 000 afin de continuer avec la préparation du questionnaire.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  socialInsuranceNumber: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro de téléphone résidentiel',
        en: '',
      },
      tooltip: {
        fr: 'Le format du numéro de téléphone saisi ne sera pas vérifié par Optitax. Le numéro que vous avez saisi sera accepté tel quel.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  homePhoneNumber?: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro de téléphone cellulaire',
        en: '',
      },
      tooltip: {
        fr: 'Le format du numéro de téléphone saisi ne sera pas vérifié par Optitax. Le numéro que vous avez saisi sera accepté tel quel.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  cellPhoneNumber: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: "Est-ce qu'il y a eu un changement d'état civil au cours de l'année 2023?",
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  hasCivilStatusChangedDuringThisFiscalYear: boolean;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date du changement',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  @ValidateIf(o => o.hasCivilStatusChangedDuringThisFiscalYear === true)
  civilStatusChangeDate: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Êtes-vous citoyen canadien?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouACanadianCitizen: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous droit au crédit d\'impôt pour personne vivant seule pour l\'année d\'imposition?',
        en: '',
      },
      tooltip: {
        fr: 'Si vous avez résidé seul(e) ou avec un ou plusieurs enfants à charge pendant toute l\'année 2021, vous pouvez demander ce crédit. Cependant, vous n\'êtes pas admissible à ce crédit si :' +
        '• Vous avez partagé la résidence à tout moment en 2021 avec une autre personne, telle que votre conjoint, votre colocataire, votre mère ou votre père, un frère ou une sœur ;' +
        '• Vous avez vécu dans une chambre dans une maison de pension ou un hôtel.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isAloneAllYear: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous séjourné à l\'extérieur du Canada pendant 183 jours ou plus au cours de l\'année 2023?',
        en: '',
      },
      tooltip: {
        fr: 'Si vous n\'avez pas de liens importants avec le Canada en termes de résidence et que' +
          ' vous avez soit vécu à l\'extérieur du Canada pendant toute l\'année (à moins d\'être un ' +
          'résident réputé), soit séjourné moins de 183 jours dans l\'année d\'imposition, vous ' +
          'pourriez être considéré comme un non-résident du Canada. Si vous souhaitez obtenir ' +
          'une opinion officielle de l\'Agence du revenu du Canada sur votre statut de résidence, ' +
          'vous pouvez remplir soit le formulaire NR74 pour les entrées au Canada, soit le ' +
          'formulaire NR73 pour les sorties du Canada, selon votre situation.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouSayedOutsideCanadaMoreThan183Days: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous séjourné plus de 182 jours aux États-Unis dans les 3 dernières années?',
        en: '',
      },
      tooltip: {
        fr: 'Si un résident canadien passe 183 jours ou plus aux États-Unis pendant une année, il est généralement considéré comme un résident américain. Si un contribuable est à la fois résident canadien et résident réputé aux États-Unis, la convention fiscale Canada-États-Unis peut aider à déterminer son statut de résidence.' +
          'De plus, un résident canadien sera également considéré comme un résident des États-Unis aux fins fiscales américaines s’il a passé plus de 182 jours sur une période de trois ans, en prenant en compte le nombre total de jours passés aux États-Unis cette année-là, le tiers des jours passés l\'année précédente, et le sixième des jours passés la deuxième année précédente en plus d\'avoir passé plus de 30 jours aux États-Unis pendant l\'année en cours.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouStayedInTheUSMoreThan120Days: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que la personne est décédée au cours de l\'année 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouDeceased: boolean;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date du décès',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouDeceased === true)
  @ValidateNested()
  dateOfDeath: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom du liquidateur testamentaire',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouDeceased === true)
  testamentaryLiquidatorName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'À un moment quelconque durant l’année, avez-vous détenu des biens étrangers dont le coût total s\'est élevé à plus de 100 000 $ (dollars canadiens)?',
        en: '',
      },
      tooltip: {
        fr: 'Vous devez déclarer tous les biens étrangers, conformément au paragraphe 233.3 (1) de la Loi de l’impôt sur le revenu, comprenant ce qui suit :' +
          '• des fonds ou biens incorporels (brevets, droits d\'auteur, etc.) situés, déposés ou détenus à l\'étranger; ' +
          '• des biens corporels situés à l\'étranger; ' +
          '• une action du capital-actions d\'une société non-résidente détenue par le contribuable ou par un agent au nom du contribuable; ' +
          '• une participation dans une fiducie non-résidente qui a été acquise pour une contrepartie à l\'exception d\'une participation dans une fiducie non-résidente qui est une société étrangère affiliée aux fins de l\'article 233.4 de la Loi ; ' +
          '• une action d\'une société résidant au Canada détenue par vous ou pour vous hors du Canada; ' +
          '• une participation dans une société de personnes qui détient des biens étrangers déterminés, à moins que la société de personnes soit tenue de produire un formulaire T1135; ' +
          '• une participation dans une entité qui est non-résidente ou un droit relativement à une telle entité; ' +
          '• un bien qui est convertible en un bien étranger déterminé, ou qui est échangeable contre un tel bien, ou qui confère le droit d\'acquérir un tel bien; ' +
          '• les titres de créance d\'un non-résident, y compris les obligations de gouvernement ou de société, les débentures, les créances hypothécaires et les effets à recevoir;' +
          '• une participation dans une police d\'assurance étrangère; et ' +
          '• les métaux précieux, les certificats d\'or et les contrats à terme détenus hors du Canada. Les biens étrangers déterminés ne comprennent pas : ' +
          '• les biens utilisés ou détenus exclusivement dans le cadre des activités d\'une entreprise exploitée activement; ' +
          '• les actions du capital-actions ou les titres de créance d\'une société étrangère affiliée; ' +
          '• une participation dans une fiducie visée aux alinéas a) ou b) de la définition de « fiducie exonérée » du paragraphe 233.2(1) de la Loi ; ' +
          '• un bien à usage personnel, au sens de l\'article 54 de la Loi ; et' +
          '• une participation dans l\'un ou l\'autre des biens étrangers exclus mentionnés ci-dessus, ou le droit d\'acquérir l\'un de ces biens. Pour les questions fréquemment posées ou des exemples, consultez Questions et réponses au sujet du formulaire T1135 sur le site Web de l\'ARC',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  doYouHavePropertyAbroad: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Description du bien étranger',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHavePropertyAbroad === true)
  propertyAbroadType: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Pays où le bien étranger est situé',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHavePropertyAbroad === true)
  propertyAbroadCountry: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Voulez-vous recevoir vos avis de cotisation par courriel?',
        en: '',
      },
      tooltip: {
        fr: 'vous recevrez un avis par courriel lorsque vous aurez reçu du courrier de l’Agence que vous pouvez consulter dans Mon dossier. Vous ne recevrez pas de copie papier par la poste.' +
          'Voici quelques exemples de courrier que vous pouvez actuellement recevoir en ligne :' +
          '• Avis de cotisation' +
          '• Avis de nouvelle cotisation' +
          '• Avis de prestations' +
          '• Avis de redressement' +
          '• Rappels d’acomptes provisionnels' +
          '• Lettres sollicitant des renseignements et des documents pour certains programmes',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  doYouAcceptToReceiveYourNoticeOfAssessmentByEmail: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous versé des acomptes provisionnels pour l\'année 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouMadeAdvancedPayments: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Êtes-vous inscrit au dépôt direct?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouRegisteredForDirectDeposit: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous des revenus d\'emploi durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouReceivedAStatementOfRemunerationPaid: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous des revenus d’emploi à l’étranger durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAStatementOfRemunerationPaid === true)
  doYouHaveForeignEmploymentIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu de l\'assurance-emploi, SAAQ, assistance social ou RQAP durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouReceivedAT4E: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu des indemnités pour accident de travail(CNESST) durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouReceivedAT5007: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous droit à un remboursement de TPS/TVQ sur vos dépenses d’emploi durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveAT2200 === true)
  doYouWantToFillAGST370Application: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Êtes-vous un travailleur autonome?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouSelfEmployed: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que votre entreprise est inscrite aux taxes?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouSelfEmployed === true)
  areYouRegisteredForTaxPerception: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous fait le choix de la méthode rapide?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouRegisteredForTaxPerception === true && o.areYouSelfEmployed === true)
  haveYouChosenTheQuickMethodOfAccountingForTaxes: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que votre entreprise est inscrite au registraire des entreprises?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouSelfEmployed === true)
  areYouRegisteredForTheREQ: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quelle est la raison sociale de votre entreprise?',
        en: '',
      },
      tooltip: {
        fr: 'Si votre entreprise n\'a pas de raison sociale, inscrivez votre prénom et nom',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouSelfEmployed === true)
  legalName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous exercé des options d\'achat d\'actions de votre employeur durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAStatementOfRemunerationPaid === true)
  haveYouExercisedStockOptionsFromYourEmployer: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des cotisations professionnelles durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAStatementOfRemunerationPaid === true)
  haveYouPaidProfessionalMembershipDues: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Sont-elles payée par l\'employeur et inclus sur votre T4?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAStatementOfRemunerationPaid === true && o.haveYouPaidProfessionalMembershipDues === true)
  areTheProfessionalMembershipDuesPaidByYourEmployer: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous des dépenses d’emploi durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAStatementOfRemunerationPaid === true)
  doYouHaveAT2200: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous acquis de l’outillage pour votre métier durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAStatementOfRemunerationPaid === true)
  haveYouPurchasedTools: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous été à l\'emploi à titre de nouveau diplômé travaillant dans une région ressource éloignée durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAStatementOfRemunerationPaid === true)
  areYouANewlyGraduateWorkingInARemoteArea: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Si vous êtes un enseignant ou un éducateur de la petite enfance, avez-vous effectué des dépenses de fournitures scolaires à des fins d\'enseigement durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAStatementOfRemunerationPaid === true)
  haveYouPurchasedSchoolSupplies: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu des revenus de pension durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  doYouHavePensionIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu de la Sécurité de la vieillesse durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHavePensionIncome === true)
  haveYouReceivedOldAgeSecurityPensionIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu des rentes du Régime de rentes du Québec (RRQ) durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHavePensionIncome === true)
  haveYouReceivedRRQPensionIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu des rentes du Régime de pensions du Canada (RPC) durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHavePensionIncome === true)
  haveYouReceivedCPPRetirementPensionIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous eu des rentes d’un FERR durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHavePensionIncome === true)
  haveYouReceivedIncomeFromARRIF: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu une rente d’un régime de pension agréé (RPA) durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHavePensionIncome === true)
  haveYouReceivedIncomeFromARPP: boolean;


  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Si vous avez 70 ans ou plus au 31 décembre, avez-vous payé pour des services vous permettant de demeurer dans votre résidence de façon autonome durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidForHomeSupport: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé pour l\'achat, la location ou l\'installation d\'équipements admissibles vous permettant de demeurer dans votre résidence de façon autonome durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidForHomeSupportEquipment: boolean;


  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des dépenses pour des activités physiques, artistiques, récréatives ou culturelles durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidForSeniorActivities: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Vous et votre conjoint êtes-vous consentants à fractionner vos revenus de pension afin de réduire votre fardeau fiscal? ',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHavePensionIncome === true && (o.civilStatus === CivilStatusEnum.Married || o.civilStatus === CivilStatusEnum.LivingCommonLaw))
  doYouAgreeToSplitYourPensionIncomeWithSpouse: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous effectué des travaux de rénovation permettant l’accessibilité domiciliaire durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouDoneHomeAccessibilityImprovements: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous engagé des frais pour un séjour dans une unité transitoire de récupération fonctionnelle durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidTransitUnitStayFees: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Étiez-vous aux études durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouStudying: boolean;


  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous fait des retraits d\'un régime d\'épargne-étude ou reçu une bourse d\'étude?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouStudying === true)
  haveYouWithdrawFromARESPOrHaveYouReceivedAScholarship: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais de scolarité durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouStudying === true)
  haveYouPaidTuitionFees: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce qu\'ils ont été remboursés par l\'employeur?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouPaidTuitionFees === true)
  areYouTuitionFeesReimbursedByYourEmployer: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais pour un examen professionnel au cours de l’année 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouStudying === true)
  doYouHaveProfessionalExaminationFees: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce qu\'ils ont été remboursés par l\'employeur?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveProfessionalExaminationFees === true)
  areYourProfessionalExaminationFeesReimbursedByYourEmployer: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous subvenu aux besoins d’un parent ou d’une personne qui était à votre charge en raison d\'une déficience physique ou mentale durant l’année 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouACaregiver: boolean;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de début',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @IsOptional()
  @ValidateNested()
  @ValidateIf(o => o.areYouACaregiver === true)
  caregiverStartDate?: CalculationDate;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de fin',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @IsOptional()
  @ValidateNested()
  @ValidateIf(o => o.areYouACaregiver === true)
  caregiverEndate?: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Hébergez-vous un proche qui est incapable de vivre seul dont vous avez une attestation d\'un médecin?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouAccommodatingARelative: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Lien entre la personne et vous',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouAccommodatingARelative === true)
  relationshipWithAccommodatedRelative: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Prénom de la personne',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouAccommodatingARelative === true)
  accommodatedRelativeFirstName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom de famille de la personne',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouAccommodatingARelative === true)
  accommodatedRelativeLastName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Type de maladie',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouAccommodatingARelative === true)
  accommodatedRelativeDisabilityName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous droit au crédit pour personne handicapée durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouDisabled: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce votre première demande du crédit pour personne handicapée?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouDisabled === true)
  mainSpouseIsT2201: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des dépenses durant l\'année d\'imposition qui vous a permis de travailler ou de fréquenter un établissement d\'enseignement?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.areYouDisabled === true)
  doYouHaveExpensesThatAllowedYouToWorkOrAttendAnEducationalEstablishment: boolean;


  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous obtenu un relevé 31 de votre locateur pour l\'année 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouReceivedRL31Slip: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Étiez-vous propriétaire de votre résidence au 31 décembre 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areYouAHomeOwner: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous fait l’acquisition d’une habitation au cours de l\'année 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPurchasedAHomeThisYear: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous habité, au cours de l\'année de l\'achat ou des 4 années précédentes, dans une autre habitation dont vous ou votre époux ou conjoint de fait étiez propriétaire?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouPurchasedAHomeThisYear === true)
  inTheLast4YearsHaveYouResidedInAnotherHomeThatYouOrYourSpouseOwned: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous acheté l\'habitation pour une personne de votre famille et qui est admissible au crédit pour personnes handicapées?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouPurchasedAHomeThisYear === true)
  haveYouPurchasedTheHomeForAFamilyMemberThatIsElligibleForTheDisabilityTaxCredit: boolean;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date d\'acquisition de l\'habitation',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  @ValidateIf(o => o.inTheLast4YearsHaveYouResidedInAnotherHomeThatYouOrYourSpouseOwned === true || o.haveYouPurchasedTheHomeForAFamilyMemberThatIsElligibleForTheDisabilityTaxCredit === true)
  homePurchaseDate: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro de matricule ou désignation cadastrale inscrit sur votre compte de taxes municipales',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  @ValidateIf(o => o.inTheLast4YearsHaveYouResidedInAnotherHomeThatYouOrYourSpouseOwned === true || o.haveYouPurchasedTheHomeForAFamilyMemberThatIsElligibleForTheDisabilityTaxCredit === true)
  homeCadastralNumber?: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom des propriétaires de la résidence',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.inTheLast4YearsHaveYouResidedInAnotherHomeThatYouOrYourSpouseOwned === true || o.haveYouPurchasedTheHomeForAFamilyMemberThatIsElligibleForTheDisabilityTaxCredit === true)
  homeOwnerFirstName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous vendu votre résidence principale, secondaire ou locative au cours de l’année en cours?',
        en: '',
      },
      tooltip: {
        fr: 'Il est maintenant obligatoire de déclarer la vente de votre résidence principale, ainsi que toute désignation d\'une résidence comme étant votre résidence principale.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouSoldYourPrincipalResidence: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous déménagé au cours de l\'année 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouMovedDuringThisYear: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Vous êtes-vous rapproché d\'au moins 40 kilomètres de votre nouveau lieu de travail?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouMovedDuringThisYear === true)
  haveYouMovedAtLeast40KilometresCloserToYourWorkLocation: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Vous êtes-vous rapproché d\'au moins 40 kilomètres de votre nouveau lieu d\'études?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouMovedDuringThisYear === true)
  haveYouMovedAtLeast40KilometresCloserToYourSchoolLocation: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous déménagé dans une nouvelle habitation pour permettre à une personne handicapée de vivre dans une habitation plus accessible ou dans un environnement mieux adapté à ses besoins?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouMovedDuringThisYear === true)
  haveYouMovedInANewResidenceToAllowADisabledPersonToLiveInAMoreAccessibleLocation: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des montants pour des rénovations admissibles pour permettre l\'accès au logement ou réduire le risque de blessures à l\'intérieur du logement admissible durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  mainSpouseAreRenovationsForDisability: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous eu des revenus, pertes ou dépenses relatives à des actions, biens locatifs ou tout autre forme de placement?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  doYouHaveAnyInvestments: boolean;


  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous des revenus de placement provenant d\'un compte non-enregistré durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveAnyInvestments === true)
  haveYouReceivedAStatementOfInvestmentIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous eu des gains ou pertes en capital provenant de la vente d\'actions?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveAnyInvestments === true)
  doYouHaveCapitalGainsOrLosses: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous des revenus provenant d\'une société en commandite ou d\'une société de personnes durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveAnyInvestments === true)
  haveYouReceivedAStatementOfPartnershipIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous disposé d’actions provenant de la réalisation d’options d’achats d’actions de votre employeur durant l\'année d\'imposition',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveAnyInvestments === true)
  haveYouDisposedOfStockOptions: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous effectué le choix de reporter un avantage imposable relatif à l’emploi au moment de la disposition de ces actions?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouDisposedOfStockOptions === true)
  haveYouChosenToReportTheStockOptionsDisposal: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous des pertes au titre de placement d’entreprise durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveAnyInvestments === true)
  doYouHaveBusinessInvestmentLosses: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous un revenu de location durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveAnyInvestments === true)
  doYouHaveARentalIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des intérêts pour investir dans des placements, payer des frais de courtages, des frais de gestion de placement ou des honoraires professionnels déductibles durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.doYouHaveAnyInvestments === true)
  haveYouPaidInterestOnInvestments: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous effectué des retrait d’un REER durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouWithdrawnFromARRSP: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous cotisé à des REER au cours de l\'année ou dans les 60 jours suivant la fin d\'année 2023?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouContributedToARRSP: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: ' Avez-vous investi dans un fonds de travailleurs ou Capital Régional coopératif Desjardins durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouContributedToARRSP === true)
  haveYouPurchasedLabourSponsoredVentureCapitalCorporationShares: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous effectué un retrait en utilisant le programme de régime d\'accession à la propriété (RAP) durant l\'année d\'imposition ou dans une année précédente?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  mainSpouseIsRAP: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Année du retrait RAP',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  @Max(2023, { buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.mainSpouseIsRAP === true)
  mainSpouseRAPYear?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant du retrait RAP',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  @ValidateIf(o => o.mainSpouseIsRAP === true)
  mainSpouseRAPAmount?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous  effectué un retrait en utilisant le programme du régime d\'encouragement à l\'étude permanente (REEP) durant l\'année d\'imposition ou dans une année précédente?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  mainSpouseIsREEP: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Année du retrait REEP',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.mainSpouseIsREEP === true)
  mainSpouseREEPYear: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant du retrait RAP',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.mainSpouseIsREEP === true)
  mainSpouseREEPAmount: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais médicaux ou dentaires, non remboursés par un assureur durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  doYouHaveNonReimbursedMedicalExpenses: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Si vous avez 65 ans ou plus au 31 décembre, deviez-vous être inscrit à la RAMQ durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  didYouNeedToBeRegisteredToTheRAMQDrugInsurance: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que la prime d\'assurance est remboursée par votre employeur durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouCoveredByAPrivateDrugInsurance === true)
  wasYourInsurancePremiumReimbursedByYourEmployer: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Janvier?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseJanuary: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Février',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseFebruary: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Mars',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseMarch: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avril',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseApril: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Mai',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseMay: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Juin',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseJune: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Juillet',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseJuly: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Août',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseAugust: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Septembre',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseSeptember: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Octobre',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseOctober: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Novembre',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseNovember: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Décembre',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.wereYouEnrolledInADrugInsurancePlan === PrescriptionDrugInsuranceEnum.PartOfTheYear)
  mainSpouseDecember: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu une pension alimentaire durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouReceivedAlimony: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que vous avez reçu une pension alimentaire pour vos enfants de la part de votre ex-conjoint durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAlimony === true)
  haveYouReceivedChildSupport: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant de pension alimentaire reçu pour vos enfants de la part de votre ex-conjoint durant l\'année d\'imposition',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  @ValidateIf(o => o.haveYouReceivedAlimony === true && o.haveYouReceivedChildSupport === true)
  childSupportAmount?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que vous avez reçu une pension alimentaire pour votre votre compte de la part votre ex-conjoint durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouReceivedAlimony === true)
  mainSpouseIsAlimonyForExReceived: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Prénom et nom de votre ex-conjoint',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  @ValidateIf(o => o.haveYouReceivedAlimony === true && o.mainSpouseIsAlimonyForExReceived === true)
  mainSpouseExName?: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Adresse de votre ex-conjoint durant l\'année d\'imposition',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  @ValidateIf(o => o.haveYouReceivedAlimony === true && o.mainSpouseIsAlimonyForExReceived === true)
  mainSpouseExAddress?: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro d\'assurance social (NAS) de votre ex-conjoint',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  @ValidateIf(o => o.haveYouReceivedAlimony === true && o.mainSpouseIsAlimonyForExReceived === true)
  mainSpouseExSIN?: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant de pension alimentaire reçu pour votre compte de votre ex-conjoint durant l\'année d\'imposition',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional()
  @ValidateIf(o => o.haveYouReceivedAlimony === true && o.mainSpouseIsAlimonyForExReceived === true)
  mainSpouseAlimonyAmountForExReceived?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Payez-vous une pension alimentaire durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidAlimonyOrChildSupport: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Payez-vous une pension alimentaire pour vos enfants durant l\'année d\'imposition? (montant non déductible)',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouPaidAlimonyOrChildSupport === true)
  haveYouPaidChildSupport: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Payez-vous une pension alimentaire à votre ex-conjoint pour son compte durant l\'année d\'imposition? (montant déductible)',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouPaidAlimonyOrChildSupport === true)
  haveYouPaidAlimony: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais juridiques pour l\'établissement ou la révision d\'une pension alimentaire durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidLegalFeesRelatedToTheAlimonyOrChildSupport: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous vendu, cédé, échangé de la monnaie virtuelle durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  mainSpouseAreVirtualCurrency: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu d\'autres revenus, avec ou sans feuillets durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouEarnedAnyOtherIncome: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous reçu des versements anticipés de crédits d’impôts durant l\'année d\'imposition (Ex: maintien à domicile des aînés, frais de garde, prime au travail)?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouReceivedAdvancePaymentsOfTaxCredits: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous effectué des dons de charité durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouMadeAnyDonations: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous fait des contributions politiques durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouMadePoliticalContributions: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des intérêts sur un prêt étudiant durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidStudentLoanInterests: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des honoraires professionnels durant l\'année d\'imposition (frais d’opposition, projet de cotisation, cotisation préparation de la déclaration fiscale)',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidProfessionalFees: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous engagé des frais relatifs à un traitement d\'infertilité/fécondité durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidInfertilityRelatedFees: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais d\'adoption durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouPaidAdoptionFees: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom de l\'enfant adopté',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.haveYouPaidAdoptionFees === true)
  adoptedChildFullName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous effectué des travaux portant sur des installations d\'assainissement des eaux usées résidentielles durant l\'année d\'imposition?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  haveYouMadeWaterPurificationUpgrades: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quelle était votre province de résidence au 31 décembre?',
        en: '?',
      },
      tooltip: {
        fr: 'Sélectionnez l\'option appropriée pour indiquer la province ou le territoire dans lequel vous résidiez le 31 décembre' +
          '' +
          'Dans des situations particulières:' +
          '' +
          'Si vous avez quitté le Canada en 2021, sélectionnez la province ou le territoire dans lequel vous résidiez le jour de votre départ du Canada.' +
          'Si une personne est décédée en 2021, sélectionnez la province ou le territoire dans lequel elle résidait le jour de son décès.',
      },
    },
    items: [
      {
        label: {
          fr: 'Québec',
          en: '',
        },
        id: ProvinceEnum.Quebec,
      },
      {
        label: {
          fr: 'Ontario',
          en: '',
        },
        id: ProvinceEnum.Ontario,
      },
      {
        label: {
          fr: 'Alberta',
          en: '',
        },
        id: ProvinceEnum.Alberta,
      },
      {
        label: {
          fr: 'Colombie-Britannique',
          en: '',
        },
        id: ProvinceEnum.BritishColumbia,
      },
      {
        label: {
          fr: 'Île-du-Prince-Edouard',
          en: '',
        },
        id: ProvinceEnum.PrinceEdwardIsland,
      },
      {
        label: {
          fr: 'Manitoba',
          en: '',
        },
        id: ProvinceEnum.Manitoba,
      },
      {
        label: {
          fr: 'Nouveau-Brunswick',
          en: '',
        },
        id: ProvinceEnum.NewBrunswick,
      },
      {
        label: {
          fr: 'Nouvelle-Écosse',
          en: '',
        },
        id: ProvinceEnum.NovaScotia,
      },
      {
        label: {
          fr: 'Saskatchewan',
          en: '',
        },
        id: ProvinceEnum.Saskatchewan,
      },
      {
        label: {
          fr: 'Terre-Neuve-Et-Labrador',
          en: '',
        },
        id: ProvinceEnum.Newfoundland,
      },
      {
        label: {
          fr: 'Yukon',
          en: '',
        },
        id: ProvinceEnum.Yukon,
      },
      {
        label: {
          fr: 'Nunavut',
          en: '',
        },
        id: ProvinceEnum.Nunavut,
      },
    ],
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsEnum(ProvinceEnum, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  province: ProvinceEnum;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Étiez-vous couvert par une assurance-médicaments offerte par un régime d\'assurance collective durant l\'année d\'imposition?',
        en: '?',
      },
    },
    items: [
      {
        label: {
          fr: 'Oui, toute l\'année',
          en: '',
        },
        id: PrescriptionDrugInsuranceEnum.WholeYear,
      },
      {
        label: {
          fr: 'Oui, une partie de l\'année',
          en: '',
        },
        id: PrescriptionDrugInsuranceEnum.PartOfTheYear,
      },
      {
        label: {
          fr: 'Non',
          en: '',
        },
        id: PrescriptionDrugInsuranceEnum.NeverInsured,
      },
    ],
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsEnum(PrescriptionDrugInsuranceEnum, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  wereYouEnrolledInADrugInsurancePlan: PrescriptionDrugInsuranceEnum;
}
